<template>
  <header class="mb-12 bg-surface-3 border-t border-b border-border-subtle text-text-primary">
  <nav class="container flex items-center justify-between py-5 px-4 sm:px-6 lg:px-8 mx-auto" id="main-header-nav">
    <component
      :is="isPublicSite ? RouterLink : 'a'"
      :to="isPublicSite ? '/' : undefined"
      :href="isPublicSite ? undefined : publicHomeHref"
      class="branding-link flex items-center space-x-4 branding hover:text-accent-text"
    >
      <!-- The mark is a near-black SVG with baked-in fills; invert it for the dark
           header rather than shipping a second asset. -->
      <img src="../assets/skyspeaksicon.svg" alt="The Sky Speaks Logo" class="w-10 dark:invert" width="229">
      <h1 class="text-2xl">{{ DEFAULT_TITLE }}</h1>
    </component>

    <!-- Public site: session-blind, always the same regardless of any app-host session. -->
    <div v-if="isPublicSite" class="hidden space-x-1 text-lg md:flex">
      <RouterLink to="/current-sky" class="hover:text-accent-text py-3 px-4 min-h-[44px] flex items-center">Current Sky</RouterLink>
      <RouterLink to="/about" class="hover:text-accent-text py-3 px-4 min-h-[44px] flex items-center">About</RouterLink>
      <a :href="loginHref" class="hover:text-accent-text py-3 px-4 min-h-[44px] flex items-center">Login</a>
      <a :href="joinHref" class="hover:text-accent-text py-3 px-4 min-h-[44px] flex items-center">Join</a>
    </div>
    <!-- App host, authenticated member. -->
    <div v-else-if="authState.isAuthenticated" class="hidden items-center space-x-1 text-lg md:flex">
      <RouterLink to="/current-sky" class="hover:text-accent-text py-3 px-4 min-h-[44px] flex items-center">Current Sky</RouterLink>
      <RouterLink to="/personal-sky" class="hover:text-accent-text py-3 px-4 min-h-[44px] flex items-center">Personal Sky</RouterLink>
      <MainHeaderUserMenu :label="menuLabel" @logout="logout" />
    </div>
    <!-- App host, unauthenticated (mid login/join/verification flow): Current Sky is a
         member-only feature on this host (see routes.js's requiresAuthOnAppHost), so
         it is not shown here -- no app nav items until the member is authenticated. -->
    <div v-else class="hidden md:flex"></div>

    <button @click="isOpen = !isOpen" class="md:hidden min-h-[44px] min-w-[44px] flex flex-col items-center justify-center">
      <span class="block w-5 h-0.5 bg-text-primary mb-1"></span>
      <span class="block w-5 h-0.5 bg-text-primary mb-1"></span>
      <span class="block w-5 h-0.5 bg-text-primary"></span>
    </button>
  </nav>

  <div v-if="isOpen" class="md:hidden fixed inset-0 z-40 flex">
    <div class="flex-1 bg-black/30" @click="isOpen = false" aria-hidden="true"></div>
    <div class="w-64 max-w-[85vw] bg-surface-3 text-text-primary shadow-lg min-h-full p-5 pt-14">
      <button @click="isOpen = false" class="absolute top-4 right-4 min-h-[44px] min-w-[44px] flex items-center justify-center text-text-secondary hover:text-text-primary" aria-label="Close menu">
        <i class="fa-solid fa-times text-xl"></i>
      </button>
      <ul v-if="isPublicSite" class="flex flex-col gap-1">
        <li><RouterLink to="/current-sky" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">Current Sky</RouterLink></li>
        <li><RouterLink to="/about" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">About</RouterLink></li>
        <li><a :href="loginHref" class="block py-3 px-4 min-h-[44px] flex items-center">Login</a></li>
        <li><a :href="joinHref" class="block py-3 px-4 min-h-[44px] flex items-center">Join</a></li>
      </ul>
      <ul v-else-if="authState.isAuthenticated" class="flex flex-col gap-1">
        <li><RouterLink to="/current-sky" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">Current Sky</RouterLink></li>
        <li><RouterLink to="/personal-sky" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">Personal Sky</RouterLink></li>
        <li class="my-2 border-t border-border-subtle" aria-hidden="true"></li>
        <li><RouterLink to="/profile" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">Profile</RouterLink></li>
        <li><RouterLink to="/account" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">Account</RouterLink></li>
        <li v-if="showPersonalSkyDisplaySettings">
          <button
            type="button"
            class="w-full text-left py-3 px-4 min-h-[44px]"
            @click="openDisplaySettings"
          >
            Display Settings
          </button>
        </li>
        <li v-if="authState.isAdmin">
          <RouterLink to="/admin/users" @click="isOpen = false" class="block py-3 px-4 min-h-[44px] flex items-center">Admin</RouterLink>
        </li>
        <li><button @click="logout" class="w-full text-left py-3 px-4 min-h-[44px]">Logout</button></li>
      </ul>
      <ul v-else class="flex flex-col gap-1"></ul>
    </div>
  </div>

</header>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { RouterLink, useRoute, useRouter } from "vue-router";
  import { DEFAULT_TITLE } from '@/utils/constants.js';
  import { authState, checkAuth } from '@/utils/auth';
  import { performLogout, LOGOUT_UNCONFIRMED_MESSAGE } from '@/utils/logout';
  import { useChartDisplaySettings } from '@/composables/useChartDisplaySettings';
  import { useUserMenuIdentity } from '@/composables/useUserMenuIdentity';
  import MainHeaderUserMenu from '@/components/MainHeaderUserMenu.vue';
  import { isPublicSite, publicUrl, appUrl } from '@/utils/siteContext';

  const route = useRoute();
  const router = useRouter();
  const isOpen = ref(false);
  const { menuLabel } = useUserMenuIdentity();
  const { openPanel } = useChartDisplaySettings();

  // The logo always returns to the public home page. On the public site itself this is
  // an ordinary same-origin RouterLink (preserves the existing active-state styling);
  // from the app host it must be a real cross-origin navigation.
  const publicHomeHref = publicUrl('/');
  const loginHref = appUrl('/');
  const joinHref = appUrl('/signup');

  const showPersonalSkyDisplaySettings = computed(() => route.name === 'personal-sky');

  function openDisplaySettings() {
    isOpen.value = false;
    openPanel();
  }

  const logout = async () => {
    isOpen.value = false;
    // Local session state is cleared regardless; only navigate to the normal
    // post-logout landing when the server confirmed. If it did not, send the
    // member to the login page with a notice so they retry before trusting the
    // session is ended.
    const { serverConfirmed } = await performLogout();
    router.replace(
      serverConfirmed
        ? '/'
        : { name: 'login', query: { message: LOGOUT_UNCONFIRMED_MESSAGE } },
    );
  };

  onMounted(() => {
    checkAuth();
  });

</script>

<style lang="scss" scoped>
  a {
    text-decoration: none;
  }

  .branding-link {
    background-color: transparent;

    &.router-link-active {
      color: inherit;
      background-color: transparent;
      pointer-events: auto;
      cursor: pointer;
    }
  }
</style>
